import React, { useState, useEffect, useMemo } from "react";

import { valueToString } from "helpers/Helpers";

import { useHistory } from "react-router-dom";

import Page from "components/page/Page";
import Card from "components/card/Card";

import { StatusCode } from "api/protocols";

import { useTranslation } from "react-i18next";
import { GetCardReaders } from "api/rpc/2024-04/facilityAdmin/payment/teeonPayments/reader";
import axios, { CancelToken } from "axios";
import DataTable from "../../../../../components/dataTable/DataTable";
import Input from "components/form/input/Input";
import DropFilter from "components/dropdown/DropFilter";
import { Badge } from "components/badge/Badge";
import Avatar from "components/avatar/Avatar";
import { ButtonNew as Button } from "components/buttonNew";
import { TCardReader } from "redux/reducers/models/facility";
import Tabs from "components/tabs/Tabs";
import Divider from "components/divider";

const TABLE_LIMIT = 50;

const DOCK_HUB_DEVICE_TYPES = ["bbpos_wisepos_e_dock", "stripe_s700_hub", "stripe_s700_dock"];

enum tabSelection {
  CARD_READERS = 0,
  DOCKS_AND_HUBS = 1,
}

export default function CardReaders() {
  const history = useHistory();
  const { t } = useTranslation();
  const [selected, setSelected] = useState<tabSelection>(tabSelection.CARD_READERS);

  const tabs = [
    {
      id: "card-readers",
      content: t("Card Readers"),
    },
    {
      id: "docks-and-hubs",
      content: t("Other hardware"),
    },
  ];

  function navigateToNewCardReader() {
    history.push("/admin/settings/card_reader/new");
  }

  function hardwareImage(type: string) {
    switch (type) {
      case "bbpos_wisepos_e":
        return "/public/images/hardware/stripe_wisepos.png";
      case "bbpos_wisepad_3":
        return "/public/images/hardware/stripe_wisepad.png";
      case "bbpos_wisepos_e_dock":
        return "/public/images/hardware/stripe_wisepos_dock.png";
      case "stripe_s700":
        return "/public/images/hardware/stripe_s700.png";
      case "stripe_s700_hub":
        return "/public/images/hardware/stripe_s700_hub.png";
      case "stripe_s700_dock":
        return "/public/images/hardware/stripe_s700_dock.png";
        case "stripe_s710":
          return "/public/images/hardware/stripe_s700.png";
    }
  }

  function statusBadge(status: string) {
    switch (status) {
      case "online":
        return (
          <Badge size="small" type="success">
            {valueToString(status)}
          </Badge>
        );
      case "broken":
        return (
          <Badge size="small" type="error">
            {valueToString(status)}
          </Badge>
        );
      case "deployed":
        return (
          <Badge size="small" type="warning">
            {valueToString(status)}
          </Badge>
        );
      default:
        return status ? (
          <Badge size="small" type="gray">
            {valueToString(status)}
          </Badge>
        ) : null;
    }
  }

  function navigateTo(url: string) {
    history.push(url);
  }

  const [state, setState] = useState<{cardReaders: TCardReader[]}>({
    cardReaders: undefined,
  });

  const [filterState, setFilterState] = useState({
    search: "",
    offset: 0,
    status: [],
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadCardReaders(source.token);

    const lastSelectedTab = sessionStorage.getItem("last_selected_devices_tab");
    if (lastSelectedTab === "0" || lastSelectedTab === "1") {
      setSelected(Number(lastSelectedTab));
    }
    return () => source.cancel();
  }, []);

  useEffect(() => {
    sessionStorage.setItem("last_selected_devices_tab", String(selected));
  }, [selected]);

  async function loadCardReaders(token: CancelToken) {
    setState(prevState => ({ ...prevState, cardReaders: undefined }));

    const res = await GetCardReaders(undefined, false, token);

    if (token && token.reason) {
      return;
    }

    if (res.status === StatusCode.OK) {
      setState(prevState => ({ ...prevState, cardReaders: res.data }));
    } else {
      setState(prevState => ({ ...prevState, cardReaders: [] }));
    }
  }

  function setHardwareStatusFilter(filterData: Array<Record<string, any>>) {
    if (filterData.length === 0) {
      setFilterState(prev => ({ ...prev, status: [], offset: 0 }));
      return;
    }

    const validStatuses = filterData.map(status => status.value as string);

    setFilterState(prev => ({ ...prev, status: validStatuses, offset: 0 }));
  }

  const filterForStatusHardwareList = [
    { value: "online", title: t("Online") },
    { value: "offline", title: t("Offline") },
    { value: "inventory", title: t("Inventory") },
    { value: "broken", title: t("Broken") },
    { value: "warranty", title: t("Warranty") },
    { value: "deployed", title: t("Deployed") },
  ];

  const filteredReaders = useMemo(() => {
    if (!state.cardReaders) {
      return undefined;
    }

    const query = filterState.search.trim().toLowerCase();

    return state.cardReaders.filter(reader => {
      const isDockOrHub = DOCK_HUB_DEVICE_TYPES.includes(reader.device_type);
      const tabMatch = selected === tabSelection.DOCKS_AND_HUBS ? isDockOrHub : !isDockOrHub;
      if (!tabMatch) {
        return false;
      }

      if (filterState.status.length > 0 && !filterState.status.includes(reader.status)) {
        return false;
      }

      if (query) {
        const haystack = `${reader.label ?? ""} ${reader.serial_number ?? ""} ${reader.device_type ?? ""}`.toLowerCase();
        if (!haystack.includes(query)) {
          return false;
        }
      }

      return true;
    });
  }, [state.cardReaders, selected, filterState.search, filterState.status]);

  const pagedReaders = filteredReaders?.slice(filterState.offset, filterState.offset + TABLE_LIMIT);
  const tableTitle = selected === tabSelection.CARD_READERS ? t("Card Readers") : t("Other hardware");

  const handleTabChange = (selectedTabIndex: number) => {
    setSelected(selectedTabIndex);
    setFilterState(prev => ({ ...prev, offset: 0 }));
  };

  const titleDetails = [
    {
      content: (
        <DropFilter
          title={t("Status")}
          filterData={filterForStatusHardwareList}
          filterLabelPropFromData="title"
          filterIdPropFromData="value"
          filterType="Checkbox"
          applyFilters={setHardwareStatusFilter}
          save
          historyKey="hardware_all"
          leftAligned={selected === tabSelection.CARD_READERS}
          rightAligned={selected === tabSelection.DOCKS_AND_HUBS}
        />
      ),
    },
    ...(selected === tabSelection.CARD_READERS
      ? [
          {
            content: (
              <Button type="primary" size="medium" onClick={navigateToNewCardReader}>
                {`+ ${t("New Card Reader")}`}
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <Page
      title={t("Hardware")}
      subtitle={
        selected === tabSelection.CARD_READERS
          ? t("Set up a new card reader for your facility")
          : t("View other hardware for your facility")
      }
    >
      <Divider />
      <Page.Section>
        <div style={{ display: "flex", overflowX: "auto", paddingBottom: 24 }}>
          <Tabs tabs={tabs} selected={selected} onSelect={handleTabChange} fullWidth variant="wide" />
        </div>
      </Page.Section>

      <div className="pb-2">
        <Input
          placeholder={t("Search by serial number or facility name")}
          value={filterState.search}
          onChange={e => setFilterState(prev => ({ ...prev, search: e.target.value, offset: 0 }))}
        />
      </div>

      <Card
        title={
          <div className="flex items-center gap-2">
            {tableTitle}{" "}
            <Badge type={"public"} size="medium">
              {filteredReaders?.length}
            </Badge>
          </div>
        }
        titleDetails={titleDetails}
      >
        <div className="pt-3">
          <DataTable
            columns={[
              { label: t("Label"), width: "25%" },
              { label: t("Device Type"), width: "25%" },
              { label: t("Serial Number"), width: "25%" },
              { label: t("Status"), width: "25%" },
            ]}
            loading={filteredReaders === undefined}
            footer={{
              tableLimit: TABLE_LIMIT,
              tableOffset: filterState.offset,
              disableNextOffset: !pagedReaders || !(pagedReaders?.length === TABLE_LIMIT),
              handleTableOffset: direction =>
                setFilterState(prev => ({
                  ...prev,
                  offset: direction === "prev" ? prev.offset - TABLE_LIMIT : prev.offset + TABLE_LIMIT,
                })),
            }}
            overflow
          >
            {pagedReaders?.map((reader, index: number) => (
              <tr key={index} onClick={() => navigateTo("/admin/settings/card_reader/" + String(reader.id))} className="clickable">
                <td>
                  <div className="flex items-center gap-4">
                    <Avatar source={hardwareImage(reader.device_type)} size="medium" />
                    <span className="table-cell-text">
                      <p className="lead-text">{reader.label}</p>
                    </span>
                  </div>
                </td>
                <td>{valueToString(reader.device_type)}</td>
                <td>{reader.serial_number}</td>
                <td>{statusBadge(reader.status)} {reader.aod_device ? <Badge size="small" type="primary"> {t("App Enabled")} </Badge> : null}</td>
              </tr>
            ))}
          </DataTable>
        </div>
      </Card>
    </Page>
  );
}
